// Measurement history: a compact list of per-entry mini-cards. Tap a card
// to edit that entry (the form above reuses itself as the editor); ✕
// deletes (via the injected confirm seam). Lifted to @heraion/ui (S3):
// theme + card styles are injected, Alert → confirm seam, so it runs on the
// web console too.
import { useMemo } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import {
  formatWeight,
  type Measurement,
  type UnitsPreference,
} from "@heraion/shared";

import { weightUnitLabels } from "../../weightUnitLabels";
import { formatDayMonthYear } from "../../Logger/formatDate";
import type { UiTheme } from "../../theme";
import { makeSettingsCardStyles } from "../settingsCardStyles";
import type { ConfirmFn } from "../../seams";
import { LoadMoreRow } from "../../LoadMoreRow";

export type MeasurementHistoryProps = {
  theme: UiTheme;
  // Newest-first, as the API returns them.
  items: Measurement[];
  units: UnitsPreference;
  locale: string;
  onEdit: (m: Measurement) => void;
  onDelete: (id: string) => void;
  confirm: ConfirmFn;
  /** PG-37 — the server holds rows past the ones above. Omitted (or false)
   *  on a surface that has genuinely loaded everything. */
  hasMore?: boolean;
  /** A page is in flight. */
  loadingMore?: boolean;
  onLoadMore?: () => void;
};

export function MeasurementHistory({
  theme,
  items,
  units,
  locale,
  onEdit,
  onDelete,
  confirm,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}: MeasurementHistoryProps) {
  const { t } = useTranslation();
  const card = useMemo(() => makeSettingsCardStyles(theme), [theme]);
  const styles = useMemo(() => makeStyles(theme), [theme]);

  if (items.length === 0) {
    return (
      <View style={card.card}>
        <Text style={card.cardHelp}>
          {t("mobile.body.historyEmpty", {
            defaultValue: "No measurements yet. Save one above to start tracking.",
          })}
        </Text>
      </View>
    );
  }

  const confirmDelete = async (m: Measurement) => {
    const ok = await confirm({
      title: t("mobile.body.deleteTitle", { defaultValue: "Delete measurement?" }),
      body: formatDayMonthYear(new Date(m.takenAt), locale),
      confirmLabel: t("mobile.body.deleteConfirm", { defaultValue: "Delete" }),
      cancelLabel: t("mobile.body.deleteCancel", { defaultValue: "Cancel" }),
    });
    if (ok) onDelete(m.id);
  };

  return (
    <View style={card.card}>
      <Text style={card.cardLabel}>
        {t("mobile.body.historyTitle", { defaultValue: "HISTORY" })}
      </Text>
      {items.map((m) => (
        <Pressable
          key={m.id}
          accessibilityRole="button"
          accessibilityLabel={t("mobile.body.editRowA11y", {
            defaultValue: "Edit measurement {{date}}",
            date: formatDayMonthYear(new Date(m.takenAt), locale),
          })}
          onPress={() => onEdit(m)}
          style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
        >
          <View style={styles.rowText}>
            <Text style={styles.rowDate}>{formatDayMonthYear(new Date(m.takenAt), locale)}</Text>
            <Text style={styles.rowSummary}>{describe(m, units, locale, t)}</Text>
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("mobile.body.deleteConfirm", { defaultValue: "Delete" })}
            hitSlop={8}
            onPress={() => void confirmDelete(m)}
            style={({ pressed }) => [styles.del, pressed && styles.delPressed]}
          >
            <Text style={styles.delText}>✕</Text>
          </Pressable>
        </Pressable>
      ))}
      <LoadMoreRow
        theme={theme}
        hasMore={hasMore}
        loading={loadingMore}
        onPress={() => onLoadMore?.()}
      />
    </View>
  );
}

// Field render order for the row summary (weight first, then head-to-toe
// circumferences) — each measured field shown LABELLED with its value.
const SUMMARY_FIELDS: Array<{
  key: keyof Measurement;
  labelKey: string;
  kind: "weight" | "cm";
}> = [
  { key: "weightKg", labelKey: "mobile.body.weight", kind: "weight" },
  { key: "heightCm", labelKey: "mobile.body.field_heightCm", kind: "cm" },
  { key: "neckCm", labelKey: "mobile.body.field_neckCm", kind: "cm" },
  { key: "chestCm", labelKey: "mobile.body.field_chestCm", kind: "cm" },
  { key: "waistCm", labelKey: "mobile.body.field_waistCm", kind: "cm" },
  { key: "hipsCm", labelKey: "mobile.body.field_hipsCm", kind: "cm" },
  { key: "bicepsLCm", labelKey: "mobile.body.field_bicepsLCm", kind: "cm" },
  { key: "bicepsRCm", labelKey: "mobile.body.field_bicepsRCm", kind: "cm" },
  { key: "forearmsLCm", labelKey: "mobile.body.field_forearmsLCm", kind: "cm" },
  { key: "forearmsRCm", labelKey: "mobile.body.field_forearmsRCm", kind: "cm" },
  { key: "thighsLCm", labelKey: "mobile.body.field_thighsLCm", kind: "cm" },
  { key: "thighsRCm", labelKey: "mobile.body.field_thighsRCm", kind: "cm" },
  { key: "calvesLCm", labelKey: "mobile.body.field_calvesLCm", kind: "cm" },
  { key: "calvesRCm", labelKey: "mobile.body.field_calvesRCm", kind: "cm" },
];

type TranslateFn = (key: string, opts?: Record<string, unknown>) => string;

const describe = (
  m: Measurement,
  units: UnitsPreference,
  locale: string,
  t: TranslateFn,
): string => {
  const unitLabels = weightUnitLabels(t);
  const parts: string[] = [];
  for (const f of SUMMARY_FIELDS) {
    const v = m[f.key];
    if (typeof v !== "number") continue;
    const label = t(f.labelKey, { defaultValue: String(f.key) });
    parts.push(
      f.kind === "weight"
        ? `${label} ${formatWeight(v, units, locale, unitLabels)}`
        : `${label} ${v}`,
    );
  }
  return parts.join(" · ") || "—";
};

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    row: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s3,
      paddingVertical: s.s2,
      paddingHorizontal: s.s3,
      minHeight: 48,
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: r.r2,
      marginBottom: s.s2,
    },
    rowPressed: { backgroundColor: p.surface, opacity: 0.85 },
    rowText: { flex: 1, gap: 2 },
    rowDate: {
      color: p.ink,
      fontSize: ty.sizes.base,
      fontWeight: "600",
      fontVariant: ["tabular-nums"],
    },
    rowSummary: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
      fontVariant: ["tabular-nums"],
    },
    del: { width: 36, height: 36, alignItems: "center", justifyContent: "center" },
    delPressed: { opacity: 0.5 },
    delText: { color: p.danger, fontSize: ty.sizes.lg },
  });
}
